import React, { useState, useEffect, useMemo } from 'react';
import { useInventory } from '../context/InventoryContext';
import { formatDate } from '../utils/dateFormat';
import BulkUpload from '../components/BulkUpload';
import { useAuth } from '../context/AuthContext';
import { FaEdit, FaTrash, FaPlus, FaDownload } from 'react-icons/fa';
import Pagination from '../components/Pagination';

const SimInventory = () => {
  const {
    sims, addSim, updateSim, deleteSim,
    companies, subCompanies, checkSimStatuses,
    getSubCompaniesByCompany, getEntityDisplayName
  } = useInventory();

  const [form, setForm] = useState({
    mobileNo: '',
    simNo: '',
    purchaseDate: '',
    vendor: 'airtel',
    status: 'new stock',
    safecustodyDate: '',
    parentCompanyId: '',
    companyId: '',
    image: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [showBulkAddMenu, setShowBulkAddMenu] = useState(false);
  const [showBulkUpdateMenu, setShowBulkUpdateMenu] = useState(false);

  // Notification functions
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification({ message: '', type: '', visible: false });
    }, 3000);
  };

  // Get unique statuses from sims for the filter dropdown
  const uniqueStatuses = useMemo(() => {
    const statusSet = new Set();
    sims.forEach(sim => {
      if (sim.status) {
        statusSet.add(sim.status.toLowerCase().trim());
      }
    });
    return Array.from(statusSet).sort();
  }, [sims]);

  // Get unique vendors from sims for the filter dropdown
  const uniqueVendors = useMemo(() => {
    const vendorSet = new Set();
    sims.forEach(sim => {
      if (sim.vendor) {
        vendorSet.add(sim.vendor.toLowerCase().trim());
      }
    });
    return Array.from(vendorSet).sort();
  }, [sims]);

  useEffect(() => { checkSimStatuses(); }, [sims, checkSimStatuses]);

  const filteredSims = (isSuperAdmin
    ? sims
    : sims.filter(s => {
        if (!s.companyId) return true;
        return s.companyId === currentUser.companyId ||
          subCompanies.some(sub => sub.id === s.companyId && sub.parentCompanyId === currentUser.companyId);
      })
  ).filter(s => {
    // Fix: Case-insensitive status filter with proper trimming
    if (statusFilter) {
      const simStatus = String(s.status || '').toLowerCase().trim();
      const filterStatus = String(statusFilter).toLowerCase().trim();
      if (simStatus !== filterStatus) return false;
    }
    
    // Fix: Case-insensitive vendor filter with proper trimming
    if (vendorFilter) {
      const simVendor = String(s.vendor || '').toLowerCase().trim();
      const filterVendor = String(vendorFilter).toLowerCase().trim();
      if (simVendor !== filterVendor) return false;
    }
    
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return s.mobileNo.includes(q) || 
           s.simNo.toLowerCase().includes(q);
  });

  // UTILITY FOR CSV DOWNLOAD - FIXED to get company name properly
  const handleDownloadExcel = () => {
    const headers = [
      'Mobile No', 'SIM No', 'Vendor', 'Purchase Date', 
      'Installation Date', 'Status', 'Company'
    ];

    const rows = filteredSims.map(s => {
      // Get company name using getEntityDisplayName
      let companyName = getEntityDisplayName(s.companyId);
      
      // If company name is empty or '-', try to get from parentCompanyId
      if (!companyName || companyName === '-') {
        companyName = getEntityDisplayName(s.parentCompanyId);
      }
      
      // If still empty, try to find from companies list directly
      if (!companyName || companyName === '-') {
        const company = companies.find(c => String(c.id) === String(s.companyId));
        if (company) {
          companyName = company.name;
        } else {
          const parentCompany = companies.find(c => String(c.id) === String(s.parentCompanyId));
          if (parentCompany) {
            companyName = parentCompany.name;
          }
        }
      }

      return [
        s.mobileNo || '',
        s.simNo || '',
        s.vendor || '',
        s.purchaseDate || '-',
        s.installationDate || '-',
        s.status || '',
        companyName || '-'
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `SIM_Inventory_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalItems = filteredSims.length;
  const paginatedSims = filteredSims.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [isSuperAdmin, currentUser?.companyId]);

  const resetForm = () => {
  setForm({
    mobileNo: '',  // Start empty
    simNo: '',
    purchaseDate: '',
    vendor: 'airtel',
    status: 'new stock',
    safecustodyDate: '',
    parentCompanyId: '',
    companyId: '',
    image: ''
  });
  setEditingId(null);
  setShowForm(false);
  setErrors({});
};

  const validateForm = () => {
    const newErrors = {};
    if (form.mobileNo && form.mobileNo.trim() !== '') {
    if (!/^\d{13}$/.test(form.mobileNo)) {
      newErrors.mobileNo = 'Mobile No must be exactly 13 digits.';
    } else {
      const dupMobile = sims.find(
        s => s.id !== editingId && s.mobileNo === form.mobileNo.trim()
      );
      if (dupMobile) newErrors.mobileNo = 'Mobile number already exists.';
    }
  }

    if (!/^\d+$/.test(form.simNo) || form.simNo.trim() === '') {
      newErrors.simNo = 'SIM No must contain only digits.';
    } else {
      const dupSim = sims.find(
        s => s.id !== editingId && s.simNo === form.simNo.trim()
      );
      if (dupSim) newErrors.simNo = 'SIM number already exists.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ===== NEW FUNCTION: Handle status change with validation =====
  const handleStatusChange = (newStatus) => {
    // Check if trying to set to scrap or suspended
    if (newStatus === 'scrap' || newStatus === 'suspended') {
      // Confirm with user
      const confirm = window.confirm(
        `⚠️ Setting this SIM to "${newStatus}" will make it permanently unavailable for use.\n\n` +
        `This action cannot be undone. Are you sure you want to continue?`
      );
      if (!confirm) return;
    }

    // Update form status with automatic date handling
    setForm({ 
      ...form, 
      status: newStatus,
      // If status is safecustody, set safecustodyDate to today
      ...(newStatus === 'safecustody' && { 
        safecustodyDate: new Date().toISOString().split('T')[0] 
      }),
      // If status is scrap or suspended, clear safecustodyDate
      ...((newStatus === 'scrap' || newStatus === 'suspended') && { 
        safecustodyDate: '' 
      })
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const simData = {
      ...form,
      parentCompanyId: form.parentCompanyId || null,
      companyId: form.companyId || null
    };

    // Auto-set safecustody date if status is safecustody and no date provided
    if (simData.status === 'safecustody' && !simData.safecustodyDate) {
      simData.safecustodyDate = new Date().toISOString().split('T')[0];
    }

    // Clear safecustody date if status is scrap or suspended
    if (simData.status === 'scrap' || simData.status === 'suspended') {
      simData.safecustodyDate = null;
    }

    if (editingId) {
      updateSim(editingId, simData);
      showNotification(`SIM with number "${form.mobileNo}" has been updated successfully`, 'success');
    } else {
      addSim(simData);
      showNotification(`SIM with number "${form.mobileNo}" has been added successfully`, 'success');
    }
    resetForm();
  };

  const handleEdit = (sim) => {
    const selectedSubCompany = subCompanies.find(
      s => String(s.id) === String(sim.companyId)
    );

    setForm({
      mobileNo: sim.mobileNo,
      simNo: sim.simNo,
      purchaseDate: sim.purchaseDate || '',
      vendor: sim.vendor,
      status: sim.status,
      safecustodyDate: sim.safecustodyDate || '',
      parentCompanyId: selectedSubCompany?.parentCompanyId || '',
      companyId: sim.companyId || '',
      image: sim.image || ''
    });
    setEditingId(sim.id);
    setShowForm(true);
    setErrors({});
  };

  const handleDelete = (simId) => {
    const simToDelete = sims.find(s => s.id === simId);
    if (simToDelete && window.confirm(`Are you sure you want to delete SIM with number "${simToDelete.mobileNo}"?`)) {
      deleteSim(simId);
      showNotification(`SIM with number "${simToDelete.mobileNo}" has been deleted`, 'error');
    }
  };

  const validateRow = (row, index, allRows) => {
  const errs = [];
  const mobile = (row.mobileNo || '').toString().trim();
  const sim = (row.simNo || '').toString().trim();

  if (!/^\d{13}$/.test(mobile)) {
    errs.push({ column: 'Mobile No', message: 'Must be exactly 13 digits' });
  } else {
    if (sims.some(s => s.mobileNo === mobile)) {
      errs.push({ column: 'Mobile No', message: 'Mobile number already exists' });
    }
    const earlierMobile = allRows.slice(0, index).some(r => (r.mobileNo || '').toString().trim() === mobile);
    if (earlierMobile) errs.push({ column: 'Mobile No', message: 'Duplicate mobile in this file' });
  }

  if (!/^\d+$/.test(sim)) {
    errs.push({ column: 'SIM No', message: 'Must contain only digits' });
  } else {
    if (sims.some(s => s.simNo === sim)) {
      errs.push({ column: 'SIM No', message: 'SIM number already exists' });
    }
    const earlierSim = allRows.slice(0, index).some(r => (r.simNo || '').toString().trim() === sim);
    if (earlierSim) errs.push({ column: 'SIM No', message: 'Duplicate SIM in this file' });
  }

  // Validate vendor if provided
  const vendor = (row.vendor || '').toString().trim().toLowerCase();
  if (vendor && !['airtel', 'vodafone', 'bsnl', 'jio'].includes(vendor)) {
    errs.push({ column: 'Vendor', message: `Invalid vendor "${vendor}". Must be: airtel, vodafone, bsnl, jio` });
  }

  // Validate company name if provided
  const companyName = (row.companyName || '').toString().trim();
  if (companyName) {
    const companyExists = companies.some(c => c.name.toLowerCase() === companyName.toLowerCase());
    if (!companyExists) {
      errs.push({ column: 'Company Name', message: `Company "${companyName}" not found in database` });
    }
  }

  // Validate sub-company name if provided
  const subCompanyName = (row.subCompanyName || '').toString().trim();
  if (subCompanyName && companyName) {
    const parentCompany = companies.find(c => c.name.toLowerCase() === companyName.toLowerCase());
    if (parentCompany) {
      const subExists = subCompanies.some(s => 
        String(s.parentCompanyId) === String(parentCompany.id) && 
        s.name.toLowerCase() === subCompanyName.toLowerCase()
      );
      if (!subExists) {
        errs.push({ column: 'Sub-Company Name', message: `Sub-company "${subCompanyName}" not found under "${companyName}"` });
      }
    }
  }

  // Validate status if provided
  const status = (row.status || '').toString().trim().toLowerCase();
  if (status && !['new stock', 'available', 'active', 'safecustody', 'suspended', 'scrap'].includes(status)) {
    errs.push({ column: 'Status', message: `Invalid status "${status}". Must be: new stock, available, active, safecustody, suspended, scrap` });
  }

  return errs;
};

  // ===== UPDATED: Bulk Add Handler with chunk support =====
const handleBulkAdd = (rows) => {
  console.log(`📥 Processing chunk of ${rows.length} SIMs`);
  
  let addedCount = 0;
  let skippedCount = 0;
  const errors = [];

  rows.forEach((row) => {
    try {
      const mobileNo = (row.mobileNo || '').toString().trim();
      const simNo = (row.simNo || '').toString().trim();
      
      // if (!mobileNo) {
      //   skippedCount++;
      //   errors.push(`Mobile No is required`);
      //   return;
      // }

      if (!simNo) {
        skippedCount++;
        errors.push(`SIM No is required`);
        return;
      }

      // Validate mobile number format
      if (!/^\d{13}$/.test(mobileNo)) {
        skippedCount++;
        errors.push(`Mobile No "${mobileNo}" must be exactly 13 digits`);
        return;
      }

      // Check if mobile already exists
      if (sims.some(s => s.mobileNo === mobileNo)) {
        skippedCount++;
        errors.push(`Mobile No "${mobileNo}" already exists`);
        return;
      }

      // Check if SIM already exists
      if (sims.some(s => s.simNo === simNo)) {
        skippedCount++;
        errors.push(`SIM No "${simNo}" already exists`);
        return;
      }

      // Find company
      let finalCompanyId = null;
      let parentCompanyId = null;
      
      const companyName = (row.companyName || '').toString().trim();

      if (companyName) {
        const parentCompany = companies.find(c => c.name.toLowerCase() === companyName.toLowerCase());
        if (!parentCompany) {
          skippedCount++;
          errors.push(`Company "${companyName}" not found`);
          return;
        }
        
        parentCompanyId = parentCompany.id;
        finalCompanyId = parentCompany.id;

        // Check for sub-company
        const subCompanyName = (row.subCompanyName || '').toString().trim();
        
        if (subCompanyName) {
          const sub = subCompanies.find(s => 
            String(s.parentCompanyId) === String(parentCompany.id) && 
            s.name.toLowerCase() === subCompanyName.toLowerCase()
          );
          if (sub) {
            finalCompanyId = sub.id;
          } else {
            skippedCount++;
            errors.push(`Sub-company "${subCompanyName}" not found under "${companyName}"`);
            return;
          }
        }
      }

      // Validate vendor
      const vendor = (row.vendor || 'airtel').toString().toLowerCase().trim();
      if (!['airtel', 'vodafone', 'bsnl', 'jio'].includes(vendor)) {
        skippedCount++;
        errors.push(`Invalid vendor "${vendor}". Must be: airtel, vodafone, bsnl, jio`);
        return;
      }

      // Validate status
      const status = (row.status || 'new stock').toString().toLowerCase().trim();
      if (!['new stock', 'available', 'active', 'safecustody', 'suspended', 'scrap'].includes(status)) {
        skippedCount++;
        errors.push(`Invalid status "${status}"`);
        return;
      }

      // Prepare SIM data
      const simData = {
        mobileNo: mobileNo,
        simNo: simNo,
        purchaseDate: (row.purchaseDate || '').toString().trim(),
        vendor: vendor,
        status: status,
        parentCompanyId: parentCompanyId,
        companyId: finalCompanyId,
        image: '',
        installationDate: '',
        safecustodyDate: ''
      };

      // Auto-set safecustody date if status is safecustody
      if (simData.status === 'safecustody') {
        simData.safecustodyDate = new Date().toISOString().split('T')[0];
      }

      addSim(simData);
      addedCount++;
    } catch (error) {
      skippedCount++;
      errors.push(`${error.message}`);
      console.error('Error adding SIM:', error);
    }
  });

  // Return results for this chunk - THIS IS THE KEY FIX
  return { addedCount, skippedCount, errors };
};

// ===== UPDATED: Bulk Update Handler with chunk support =====
const handleBulkUpdate = (rows) => {
  console.log(`📥 Processing chunk of ${rows.length} SIM updates`);
  
  let updatedCount = 0;
  let skippedCount = 0;
  const errors = [];

  rows.forEach((row) => {
    try {
      const simNo = (row.simNo || '').toString().trim();
      if (!simNo) {
        skippedCount++;
        errors.push(`SIM No is required for update`);
        return;
      }

      const sim = sims.find(s => s.simNo === simNo);
      if (!sim) {
        skippedCount++;
        errors.push(`SIM with number "${simNo}" not found`);
        return;
      }

      const updated = { ...sim };
      let hasChanges = false;

      // Update fields if provided
      if (row.mobileNo && /^\d{13}$/.test(row.mobileNo)) {
        updated.mobileNo = row.mobileNo;
        hasChanges = true;
      }

      if (row.vendor) {
        const vendor = row.vendor.toString().toLowerCase().trim();
        if (['airtel', 'vodafone', 'bsnl', 'jio'].includes(vendor)) {
          updated.vendor = vendor;
          hasChanges = true;
        } else {
          skippedCount++;
          errors.push(`Invalid vendor "${vendor}" for SIM "${simNo}"`);
          return;
        }
      }

      if (row.status) {
        const newStatus = row.status.toString().toLowerCase().trim();
        if (!['new stock', 'available', 'active', 'safecustody', 'suspended', 'scrap'].includes(newStatus)) {
          skippedCount++;
          errors.push(`Invalid status "${newStatus}" for SIM "${simNo}"`);
          return;
        }
        updated.status = newStatus;
        hasChanges = true;
        
        if (newStatus === 'safecustody' && !updated.safecustodyDate) {
          updated.safecustodyDate = new Date().toISOString().split('T')[0];
        }
        if (newStatus === 'scrap' || newStatus === 'suspended') {
          updated.safecustodyDate = null;
        }
      }

      if (row.installationDate) {
        updated.installationDate = row.installationDate;
        hasChanges = true;
      }

      // Update company if provided
      const companyName = (row.companyName || '').toString().trim();
      if (companyName) {
        const parentCompany = companies.find(c => c.name.toLowerCase() === companyName.toLowerCase());
        if (parentCompany) {
          let finalCompanyId = parentCompany.id;
          const subCompanyName = (row.subCompanyName || '').toString().trim();
          if (subCompanyName) {
            const sub = subCompanies.find(s => 
              String(s.parentCompanyId) === String(parentCompany.id) && 
              s.name.toLowerCase() === subCompanyName.toLowerCase()
            );
            if (sub) {
              finalCompanyId = sub.id;
            } else {
              skippedCount++;
              errors.push(`Sub-company "${subCompanyName}" not found for SIM "${simNo}"`);
              return;
            }
          }
          updated.parentCompanyId = parentCompany.id;
          updated.companyId = finalCompanyId;
          hasChanges = true;
        } else {
          skippedCount++;
          errors.push(`Company "${companyName}" not found for SIM "${simNo}"`);
          return;
        }
      }

      if (hasChanges) {
        updateSim(sim.id, updated);
        updatedCount++;
      } else {
        skippedCount++;
        errors.push(`No changes provided for SIM "${simNo}"`);
      }
    } catch (error) {
      skippedCount++;
      errors.push(`${error.message}`);
      console.error('Error updating SIM:', error);
    }
  });

  // Return results for this chunk - THIS IS THE KEY FIX
  return { updatedCount, skippedCount, errors };
};

  return (
    <div>
      {/* Notification */}
      {notification.visible && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white font-medium`}>
          {notification.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Sim Inventory</h1>
        {isSuperAdmin && (
          <div className="flex items-center space-x-3">
            <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
              <FaPlus /> Add SIM
            </button>
           {/* ====== BULK ADD OPTION WRAPPER ====== */}
            <div className="relative inline-block text-left">
              <button 
                onClick={() => {
                  setShowBulkAddMenu(!showBulkAddMenu);
                  setShowBulkUpdateMenu(false);
                }} 
                className="btn-secondary"
              >
                Bulk Add
              </button>
              
              {showBulkAddMenu && (
                <div className="absolute right-0 mt-2 p-2 bg-white rounded-xl shadow-lg border border-gray-100 z-50 flex flex-col gap-2 min-w-[200px]">
                  <BulkUpload
                    title="SIM"
                    downloadLabel="Download Template"
                    uploadLabel="Bulk Add"
                    downloadData={sims.map(s => {
                      const subCompany = subCompanies.find(
                        sub => String(sub.id) === String(s.companyId)
                      );

                      const parentCompany = subCompany
                        ? companies.find(
                            c => String(c.id) === String(subCompany.parentCompanyId)
                          )
                        : companies.find(
                            c => String(c.id) === String(s.companyId)
                          );

                      return {
                        mobileNo: s.mobileNo,
                        simNo: s.simNo,
                        purchaseDate: s.purchaseDate,
                        vendor: s.vendor,
                        status: s.status,
                        companyName: parentCompany?.name || '',
                        subCompanyName: subCompany?.name || ''
                      };
                    })}
                    columns={[
                      { key: 'mobileNo', label: 'Mobile No' },
                      { key: 'simNo', label: 'SIM No' },
                      { key: 'purchaseDate', label: 'Purchase Date (YYYY-MM-DD)' },
                      { key: 'vendor', label: 'Vendor' },
                      { key: 'status', label: 'Status' },
                      { key: 'companyName', label: 'Company Name' },
                      { key: 'subCompanyName', label: 'Sub‑Company Name' }
                    ]}
                    sampleRow={{
                      mobileNo: '9876543210',
                      simNo: '8991123456789012345',
                      purchaseDate: '2025-06-15',
                      vendor: 'airtel',
                      status: 'new stock',
                      companyName: 'Example Co',
                      subCompanyName: 'Sub1'
                    }}
                    onUpload={handleBulkAdd}
                    validateRow={validateRow}
                  />
                </div>
              )}
            </div>

            {/* ====== BULK UPDATE OPTION WRAPPER ====== */}
            <div className="relative inline-block text-left">
              <button 
                onClick={() => {
                  setShowBulkUpdateMenu(!showBulkUpdateMenu);
                  setShowBulkAddMenu(false);
                }} 
                className="btn-secondary"
              >
                Bulk Update
              </button>

              {showBulkUpdateMenu && (
                <div className="absolute right-0 mt-2 p-2 bg-white rounded-xl shadow-lg border border-gray-100 z-50 flex flex-col gap-2 min-w-[200px]">
                  <BulkUpload
                    title="SIM Bulk Update"
                    downloadLabel="Download Template"
                    uploadLabel="Bulk Update" 
                    downloadData={sims.map(s => {
                      const subCompany = subCompanies.find(
                        sub => String(sub.id) === String(s.companyId)
                      );

                      const parentCompany = subCompany
                        ? companies.find(
                            c => String(c.id) === String(subCompany.parentCompanyId)
                          )
                        : companies.find(
                            c => String(c.id) === String(s.companyId)
                          );
                      return {
                        simNo: s.simNo,
                        mobileNo: s.mobileNo,
                        vendor: s.vendor,
                        status: s.status,
                        installationDate: s.installationDate,
                        companyName: parentCompany?.name || '',
                        subCompanyName: subCompany?.name || ''
                      };
                    })}     
                    columns={[
                      { key: 'simNo', label: 'SIM No' },
                      { key: 'mobileNo', label: 'Mobile No (reference only - not updated)' },
                      { key: 'status', label: 'Status (optional)' },
                      { key: 'installationDate', label: 'Installation Date (optional, YYYY-MM-DD)' },
                      { key: 'companyName', label: 'Company Name (optional)' },
                      { key: 'subCompanyName', label: 'Sub‑Company Name (optional)' },
                    ]}
                    sampleRow={{
                      simNo: '8991123456789012345',
                      mobileNo: '9876543210', 
                      status: 'available',
                      installationDate: '2026-06-01',
                      companyName: 'Example Co',
                      subCompanyName: 'Sub1',
                    }}
                   onUpload={handleBulkUpdate}
                   validateRow={() => []}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Filters & search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="rounded-xl border border-gray-300 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Statuses</option>
            {/* Dynamic status options from actual sim data */}
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-3 mb-4">
          <label className="text-sm font-medium text-gray-700">Vendor:</label>
          <select
            value={vendorFilter}
            onChange={(e) => { setVendorFilter(e.target.value); setCurrentPage(1); }}
            className="rounded-xl border border-gray-300 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Vendors</option>
            {/* Dynamic vendor options from actual sim data */}
            {uniqueVendors.map(vendor => (
              <option key={vendor} value={vendor}>{vendor.charAt(0).toUpperCase() + vendor.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by mobile or SIM number…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full"
          />
          {/* DOWNLOAD BUTTON */}
          <button onClick={handleDownloadExcel} className="btn-secondary flex items-center gap-2 hover:bg-gray-100 whitespace-nowrap px-3 py-2.5">
            <FaDownload className="text-sm" /> Download Data
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Mobile No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SIM No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Purchase Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Installation Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedSims.map(s => {
                const simStatus = String(s.status || '').toLowerCase().trim();
                return (
                  <tr key={s.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{s.mobileNo}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{s.simNo}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(s.purchaseDate)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(s.installationDate)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 capitalize">{s.vendor}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
  {simStatus === 'safecustody' ? (
    <div className="flex flex-col items-start">
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        safecustody
      </span>
      {s.safecustodyDate && (
        <span className="text-xs text-gray-500 mt-0.5">
          ({formatDate(s.safecustodyDate)})
        </span>
      )}
    </div>
  ) : (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      simStatus === 'new stock' ? 'bg-yellow-100 text-yellow-800' :
      simStatus === 'available' ? 'bg-green-100 text-green-800' :
      simStatus === 'active' ? 'bg-blue-100 text-blue-800' :
      simStatus === 'scrap' ? 'bg-red-100 text-red-800' :
      simStatus === 'suspended' ? 'bg-orange-100 text-orange-800' :
      'bg-gray-100 text-gray-800'
    }`}>
      {s.status}
    </span>
  )}
</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {getEntityDisplayName(s.companyId || s.parentCompanyId) || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {isSuperAdmin && (
                        <div className="flex space-x-2">
                          <button onClick={() => handleEdit(s)} className="text-indigo-600 hover:text-indigo-900 transition">
                            <FaEdit />
                          </button>
                          <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700 transition">
                            <FaTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {paginatedSims.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">No SIMs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
      />

      {/* Modal */}
      {showForm && isSuperAdmin && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{editingId ? 'Edit SIM' : 'Add SIM'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No (13 digits) </label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.mobileNo}
                  onChange={e => setForm({...form, mobileNo: e.target.value.replace(/\D/g, '')})}
                  maxLength={13}
                />
                {errors.mobileNo && <p className="text-red-500 text-xs mt-1">{errors.mobileNo}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SIM No (digits only) *</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.simNo}
                  onChange={e => setForm({...form, simNo: e.target.value.replace(/\D/g, '')})}
                />
                {errors.simNo && <p className="text-red-500 text-xs mt-1">{errors.simNo}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                <input
                  type="date"
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.purchaseDate}
                  onChange={e => setForm({...form, purchaseDate: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  value={form.vendor}
                  onChange={e => setForm({...form, vendor: e.target.value})}
                >
                  <option value="airtel">Airtel</option>
                  <option value="vodafone">Vodafone</option>
                  <option value="bsnl">BSNL</option>
                  <option value="jio">Jio</option>
                </select>
              </div>
              
              {/* ===== UPDATED STATUS SELECT WITH HANDLER ===== */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  value={form.status}
                  onChange={e => handleStatusChange(e.target.value)}
                >
                  <option value="new stock">New Stock</option>
                  <option value="available">Available</option>
                  <option value="active">Active</option>
                  <option value="safecustody">Safecustody</option>
                  <option value="suspended">Suspended</option>
                  <option value="scrap">Scrap</option>
                </select>
              </div>
              
              {/* ===== SAFECUSTODY DATE - READ ONLY / AUTO SET ===== */}
              {form.status === 'safecustody' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Safecustody Date (Auto-set to today)</label>
                  <input
                    type="date"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 bg-gray-50 text-gray-600 cursor-not-allowed"
                    value={form.safecustodyDate || new Date().toISOString().split('T')[0]}
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-gray-500 mt-1">This date is automatically set when status is changed to Safecustody</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company (Optional)</label>
                <select
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5"
                  value={form.parentCompanyId}
                  onChange={(e) => setForm({ ...form, parentCompanyId: e.target.value, companyId: '' })}
                >
                  <option value="">-- No Company --</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              {form.parentCompanyId && getSubCompaniesByCompany(form.parentCompanyId).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub Company</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5"
                    value={form.companyId}
                    onChange={(e) => setForm({ ...form, companyId: e.target.value })}
                  >
                    <option value="">Select Sub Company</option>
                    {getSubCompaniesByCompany(form.parentCompanyId).map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimInventory;